import React, { useState, useEffect } from 'react';
import { db } from '../firebase'; 
import { collection, query, where, onSnapshot, addDoc, serverTimestamp, orderBy, deleteDoc, doc } from 'firebase/firestore';
import { format, differenceInDays, isWithinInterval, isAfter } from 'date-fns';
import { Shield, Activity, TrendingUp, Heart, CheckCircle, Clock, PlusCircle } from 'lucide-react';

const PersonalPathway = ({ user }) => {
    const [logs, setLogs] = useState([]);
    const [newTask, setNewTask] = useState("");
    const today = new Date();

    // 1. Marketing Cycle Logic
    const getMarketingFocus = (date) => {
        const cycles = [
            { name: "Oil & Gas", start: new Date(2026, 2, 2), end: new Date(2026, 2, 15) },
            { name: "Aviation (Pilot & ATC)", start: new Date(2026, 2, 16), end: new Date(2026, 3, 29) },
            { name: "Aestheticians", start: new Date(2026, 3, 30), end: new Date(2026, 4, 12) }
        ];
        return cycles.find(c => isWithinInterval(date, { start: c.start, end: c.end })) || { name: "Aulice Core" };
    };

    // 2. Winnie's Status Logic
    const getWinnieStatus = (date) => {
        const lastPeriod = new Date(2026, 1, 26);
        const cycleDay = (differenceInDays(date, lastPeriod) % 28) + 1;
        if (cycleDay >= 1 && cycleDay <= 7) return { status: "Menstruation", mode: "SAFE", color: "text-green-400" };
        if (cycleDay >= 11 && cycleDay <= 17) return { status: "CONCEPTIONAL", mode: "MAX FERTILITY", color: "text-red-500 font-black" };
        return { status: "Luteal Phase", mode: "LESS CAPABLE", color: "text-blue-400" };
    };

    // 3. Steroid/PCT Cycle
    const steroidStart = new Date("2026-01-12");
    const pctDate = new Date("2026-03-12");
    const dayOfCycle = differenceInDays(today, steroidStart) + 1;
    const isPCT = isAfter(today, pctDate);

    // 4. Workout Rotation (Upper -> Abs -> Legs)
    const workoutRotation = ["upper", "abs", "legs"];
    const currentWorkout = workoutRotation[differenceInDays(today, steroidStart) % 3];

    const staticRoutines = {
        workout: currentWorkout === "upper" ? ["UP-SRE", "BCP+STND", "ISO Holds"] : 
                 currentWorkout === "abs" ? ["Floor-abs", "Res-Band Obliques"] : ["Legs Chair", "Legs Wall"],
        marketing: [`${getMarketingFocus(today).name} Prep`, "LinkedIn Injection", "Discord Outreach"],
        health: ["Pregnenolone/PCT Dose", "Face Massage"]
    };

    useEffect(() => {
        if (!user) return;
        const q = query(collection(db, 'personal_chores'), where('userId', '==', user.uid), orderBy('timestamp', 'desc'));
        return onSnapshot(q, (snapshot) => {
            setLogs(snapshot.docs.map(d => ({ id: d.id, ...d.data() })));
        });
    }, [user]);

    const logAction = async (group, name) => {
        await addDoc(collection(db, 'personal_chores'), {
            userId: user.uid,
            group, label: name, 
            status: 'completed',
            date: format(today, 'yyyy-MM-dd'),
            timestamp: serverTimestamp()
        });
    };

    const addCustomTask = async () => {
        if (!newTask) return;
        await addDoc(collection(db, 'personal_chores'), {
            userId: user.uid,
            group: 'custom',
            label: newTask,
            status: 'pending',
            date: format(today, 'yyyy-MM-dd'),
            timestamp: serverTimestamp()
        });
        setNewTask("");
    };

    const winnie = getWinnieStatus(today);

    return (
        <div className="max-w-md mx-auto bg-slate-950 min-h-screen pb-24 font-serif text-slate-100">
            <header className="bg-slate-900 p-6 rounded-b-3xl border-b border-yellow-600/30">
                <h1 className="text-2xl font-black text-yellow-500 uppercase italic">Pioneer <span className="text-white">Audit</span></h1>
                <div className="grid grid-cols-2 gap-3 mt-4">
                    <div className="bg-white/5 p-3 rounded-xl border border-white/10">
                        <p className={`text-xs font-black uppercase ${winnie.color}`}>{winnie.status}</p>
                        <p className="text-[9px] opacity-60 uppercase">{winnie.mode}</p>
                    </div>
                    <div className="bg-white/5 p-3 rounded-xl border border-white/10">
                        <p className="text-xs font-black uppercase truncate">{getMarketingFocus(today).name}</p>
                        <p className="text-[9px] text-yellow-500/80 uppercase">Cycle Active</p>
                    </div>
                </div>
                <div className="mt-4 flex items-center justify-between text-[10px] font-bold">
                    <span className="text-indigo-400 uppercase">{isPCT ? "PCT PHASE" : `CYCLE DAY: ${dayOfCycle}`}</span>
                    <span className="text-slate-500 uppercase">Legal Status: Extended 2032</span>
                </div>
            </header>

            <div className="p-4 space-y-6">
                {/* Manual Task Adder */}
                <div className="flex gap-2">
                    <input 
                        value={newTask} 
                        onChange={(e) => setNewTask(e.target.value)}
                        placeholder="Add industrial exercise/task..."
                        className="flex-1 bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm"
                    />
                    <button onClick={addCustomTask} className="text-yellow-500"><PlusCircle size={32}/></button>
                </div>

                {/* Display Routines */}
                {Object.entries(staticRoutines).map(([group, list]) => (
                    <section key={group} className="bg-slate-900 rounded-2xl border border-white/5">
                        <div className="bg-white/5 px-4 py-2 border-b border-white/5 text-[10px] font-bold text-slate-500 uppercase tracking-widest">{group}</div>
                        {list.map((item, idx) => (
                            <div key={idx} className="flex justify-between items-center p-4 border-b border-white/5 last:border-0">
                                <span className="text-sm">{item}</span>
                                <button onClick={() => logAction(group, item)} className="text-yellow-600"><CheckCircle size={24}/></button>
                            </div>
                        ))}
                    </section>
                ))}

                {/* Custom/History Section */}
                <section className="bg-slate-900 rounded-2xl border border-white/5 p-4">
                    <h3 className="text-[10px] font-bold text-slate-500 uppercase mb-4">Completed Actions</h3>
                    <div className="space-y-2">
                        {logs.slice(0, 5).map(log => (
                            <div key={log.id} className="text-[10px] flex justify-between border-b border-white/5 pb-1">
                                <span className="text-slate-300 uppercase">{log.label}</span>
                                <span className="text-yellow-600 font-bold">{log.date}</span>
                            </div>
                        ))}
                    </div>
                </section>
            </div>
        </div>
    );
};

export default PersonalPathway;